import { profileQueryOptions, profileSelectors } from "@stryker/core/queries";
import type { JobTypeFilter } from "@stryker/core/types/jobs";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import JobsTable from "@/components/job/JobsTable";
import MobileJobsList from "@/components/job/MobileJobsList";
import NoCreditsModal from "@/components/job/NoCreditsModal";
import SelectJobType from "@/components/job/SelectJobType";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import useOrganization from "@/hooks/useOrganization";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

export default function Jobs() {
  const navigate = useNavigate();
  const [isDesktopView, setIsDesktopView] = useState(() => {
    if (typeof window === "undefined") return true; // SSR fallback
    return window.innerWidth >= 900;
  });
  const resetCompositeState = useCallback(() => {
    useCUCompositeJobStore.getState().resetState();
  }, []);

  const resetMechanicalState = useCallback(() => {
    useCUMechanicalJobStore.getState().resetState();
  }, []);

  const [search, setSearch] = useState("");
  const [selectJobTypeOpen, setSelectJobTypeOpen] = useState(false);
  const [showNoCredits, setShowNoCredits] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlType = searchParams.get("job_type");
  const rawJobType: JobTypeFilter = urlType === "composite" || urlType === "mechanical" ? urlType : "all";

  const { data: organization } = useOrganization();
  const { data: profileAllowedJobTypes, isLoading: isLoadingProfile } = useQuery({
    ...profileQueryOptions(),
    select: profileSelectors.allowedJobTypes,
  });
  const hasCredits = !!organization?.balanceValue;
  const allowedJobTypes = profileAllowedJobTypes?.length
    ? profileAllowedJobTypes
    : (organization?.allowed_job_types ?? []);
  const isLoadingPermissions = isLoadingProfile;

  // Handle responsive behavior for 900px breakpoint
  useEffect(() => {
    const handleResize = () => {
      setIsDesktopView(window.innerWidth >= 900);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const resolvedJobType = useMemo<JobTypeFilter>(() => {
    if (allowedJobTypes.length === 1) {
      return allowedJobTypes[0];
    }
    if (allowedJobTypes.length > 1 && rawJobType !== "all" && !allowedJobTypes.includes(rawJobType)) {
      return "all";
    }
    return rawJobType;
  }, [allowedJobTypes, rawJobType]);

  const setJobType = useCallback(
    (nextJobType: JobTypeFilter) => {
      const params = new URLSearchParams(searchParams.toString());
      if (nextJobType === "all") {
        params.delete("job_type");
      } else {
        params.set("job_type", nextJobType);
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleStartCreateFlow = () => {
    if (!hasCredits) {
      setShowNoCredits(true);
      return;
    }

    resetCompositeState();
    resetMechanicalState();

    // If only one job type is allowed, navigate directly to that job creation page
    if (allowedJobTypes.length === 1) {
      const jobType = allowedJobTypes[0];
      navigate(`/jobs/create/${jobType}`);
    } else {
      // If multiple job types are allowed, show the selection modal
      setSelectJobTypeOpen(true);
    }
  };

  return (
    <div className="relative flex flex-1 flex-col">
      <div className="mb-2 flex gap-2 flex-wrap items-center md:flex-nowrap">
        <div className="flex flex-col">
          <Typography variant="display-lg" weight="semibold">
            Jobs
          </Typography>
        </div>

        <div className="md:w-inital ml-auto mt-6 flex w-full items-end gap-4 md:mt-0">
          <TextInput
            value={search}
            onChange={(e) => {
              const input = e.target as HTMLInputElement;
              return setSearch(input.value);
            }}
            name="search"
            className="ml-auto w-full md:w-80"
            label="Search"
            placeholder="Search"
          />
          <Button
            className="fixed bottom-1 left-1/2 z-40 w-1/2 max-w-[390px] translate-x-[-50%] md:static md:w-40 md:translate-x-0"
            size="lg"
            onClick={handleStartCreateFlow}
          >
            Create new job
          </Button>
        </div>
      </div>
      {isLoadingPermissions ? (
        <div className="flex min-h-[240px] items-center justify-center">
          <Spinner svgClassName="h-16 w-16" />
        </div>
      ) : (
        <>
          <Tabs
            value={resolvedJobType}
            onValueChange={(v) => setJobType(v as JobTypeFilter)}
            className="mt-3 mb-8 hidden md:block"
          >
            <TabsList>
              {allowedJobTypes.includes("composite") && <TabsTrigger value="composite">Composite</TabsTrigger>}
              {allowedJobTypes.includes("mechanical") && <TabsTrigger value="mechanical">Mechanical</TabsTrigger>}
              {allowedJobTypes.length > 1 && <TabsTrigger value="all">All</TabsTrigger>}
            </TabsList>
          </Tabs>
          {/* Mobile tabs - shown below search and button row */}
          <div className="mb-4 md:hidden">
            <Tabs value={resolvedJobType} onValueChange={(v) => setJobType(v as JobTypeFilter)}>
              <TabsList>
                {allowedJobTypes.includes("composite") && <TabsTrigger value="composite">Composite</TabsTrigger>}
                {allowedJobTypes.includes("mechanical") && <TabsTrigger value="mechanical">Mechanical</TabsTrigger>}
                {allowedJobTypes.length > 1 && <TabsTrigger value="all">All</TabsTrigger>}
              </TabsList>
            </Tabs>
          </div>
          {isDesktopView ? (
            <JobsTable search={search} jobType={resolvedJobType} onCreateJob={handleStartCreateFlow} />
          ) : (
            <MobileJobsList search={search} jobType={resolvedJobType} onCreateJob={handleStartCreateFlow} />
          )}
        </>
      )}
      <NoCreditsModal isOpen={showNoCredits} setIsOpen={setShowNoCredits} />
      <SelectJobType setOpen={setSelectJobTypeOpen} open={selectJobTypeOpen} allowedJobTypes={allowedJobTypes} />
    </div>
  );
}
